import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Printer } from 'lucide-react'
import Header from '../components/Header'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import type { WebReceiptData } from '../services/api/auth'
import { usesReceiptWord } from '../lib/genre'
import VariationLine from '../components/VariationLine'

/**
 * #1168 Web領収書 + #1207 Webレシート (明細) の2タブページ。
 *
 * 書類の整理 (#1164): 紙レシート (利用明細+売上票) は店頭印刷、領収書はここが本命。
 * - 電子発行のため収入印紙は不要 (印紙税法上、電磁的記録は課税文書にあたらない)
 * - クレジットカード決済は金銭の直接受領ではないため「クレジットカード利用」を明記
 *   (この記載がある場合、税務上も領収書としての金銭受領証明とは扱いが異なる)
 * - 宛名・但し書きはブラウザ内で入力して印刷/PDF保存する (サーバに保存しない)
 *
 * #1207 レシートタブ: 明細 (items) / 摘要メモ / EMV 情報 (#1205) を表示。
 * 紙レシートの代替 (紙は希望者のみ運用) を担うデジタルレシート。
 *
 * 2つの入口:
 * - /receipt/:transactionId — ログイン済み客の履歴から (本人認可)
 * - /r/:transactionId?t=… — 店側の決済完了 QR から (HMAC トークン認可、
 *   アカウント無し客もスマホで開ける)。既定タブはレシート
 *
 * 領収書ドキュメント本体は日本の経費精算で使われる定型のため**常に日本語**
 * (厨房伝票と同じ判断)。周辺 UI (入力ラベル・タブ・レシート) はロケール追従。
 */

function formatDateJa(iso: string | null): string {
  if (!iso) return '----年--月--日'
  const d = new Date(iso)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '--'
  const d = new Date(iso)
  return `${d.getFullYear()}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d
    .getDate()
    .toString()
    .padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`
}

const ACCOUNT_TYPE_KEYS: Record<string, string> = {
  credit: 'receipt.accountTypeCredit',
  debit: 'receipt.accountTypeDebit',
  prepaid: 'receipt.accountTypePrepaid',
}

export default function WebReceipt() {
  const navigate = useNavigate()
  const location = useLocation()
  const { transactionId } = useParams()
  const [searchParams] = useSearchParams()
  const { t, locale } = useI18n()
  // /r/:id は公開モード (店側 QR 経由、HMAC トークン認可)
  const isPublic = location.pathname.startsWith('/r/')
  const [data, setData] = useState<WebReceiptData | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [addressee, setAddressee] = useState('')
  // 但し書きの既定は「お品代として」(領収書ドキュメントは常に日本語)
  const [purpose, setPurpose] = useState('お品代として')
  // 公開モード (QR から開いた客) と ?view=receipt はレシートタブから
  const [tab, setTab] = useState<'receipt' | 'ryoshusho'>(
    isPublic || searchParams.get('view') === 'receipt' ? 'receipt' : 'ryoshusho',
  )

  useEffect(() => {
    if (!transactionId) return
    let cancelled = false
    ;(async () => {
      try {
        const r = isPublic
          ? await api.getPublicReceipt(transactionId, searchParams.get('t') || '')
          : await api.getWebReceipt(transactionId)
        if (!cancelled) setData(r)
      } catch {
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // searchParams は t/view の初期値しか使わない (再取得不要)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId, isPublic])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-warm-white safe-top safe-bottom">
        <Header title={t('receipt.pageTitle')} onBack={isPublic ? undefined : () => navigate(-1)} />
        <p className="mt-16 text-center font-body text-sm text-text-secondary">
          {t('receipt.loadFailed')}
        </p>
      </div>
    )
  }

  const isCard = data.paymentMethod === 'stripe'
  const cardSummary = (data.cardBrand || data.cardLast4)
    ? ` (${(data.cardBrand || '').toUpperCase()} **** ${data.cardLast4 || '----'})`
    : ''
  const items = data.items || []
  // #1171 税率別内訳。旧 tx (taxRows 未同梱) は taxJpy から 10% 1 行に落とす。
  const taxRows = data.taxRows?.length
    ? data.taxRows
    : [{ rate: 10, subtotal: data.amountJpy, tax: data.taxJpy }]
  const hasReduced = taxRows.some((r) => r.rate === 8)
  const itemName = (i: { name?: string; nameEn?: string }) =>
    locale === 'ja' ? (i.name || i.nameEn || '-') : (i.nameEn || i.name || '-')
  const emv = data.cardReceipt

  return (
    <div className="min-h-screen bg-warm-white safe-top safe-bottom">
      {/* 印刷時は領収書本体だけを出す */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #web-receipt-doc, #web-receipt-doc * { visibility: visible; }
          #web-receipt-doc { position: absolute; left: 0; top: 0; width: 100%;
            box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <div className="print:hidden">
        <Header title={t('receipt.pageTitle')} onBack={isPublic ? undefined : () => navigate(-1)} />
      </div>

      {/* タブ: レシート (明細) / 領収書 */}
      <div className="px-5 mt-4 print:hidden">
        <div className="flex rounded-xl bg-gray-100 p-1">
          <button
            onClick={() => setTab('receipt')}
            className={`flex-1 rounded-lg py-2 font-body text-sm font-medium transition-colors ${
              tab === 'receipt' ? 'bg-white text-text-primary shadow-sm' : 'text-text-tertiary'
            }`}
          >
            {t(usesReceiptWord(data.store.genre) ? 'receipt.tabDetail' : 'receipt.tabDetailStatement')}
          </button>
          <button
            onClick={() => setTab('ryoshusho')}
            className={`flex-1 rounded-lg py-2 font-body text-sm font-medium transition-colors ${
              tab === 'ryoshusho' ? 'bg-white text-text-primary shadow-sm' : 'text-text-tertiary'
            }`}
          >
            {t('receipt.tabRyoshusho')}
          </button>
        </div>
      </div>

      {tab === 'receipt' && (
        /* #1207 デジタルレシート — 明細 + 決済情報 (EMV #1205)。紙レシートと同内容 */
        <div className="px-5 mt-4 pb-10">
          <div className="rounded-xl bg-white shadow-card border border-gray-200 p-5 font-body text-sm">
            <div className="text-center">
              {/* #1881 店舗ロゴ。店名の上（紙のレシートと同じ位置） */}
              {data.store.logoUrl && (
                <img
                  src={data.store.logoUrl}
                  alt=""
                  className="mx-auto mb-1 h-10 w-auto max-w-[60%] object-contain"
                />
              )}
              <p className="font-display text-base font-bold text-text-primary">
                {data.store.name || 'YOKOSO PAY'}
              </p>
              {data.store.address && (
                <p className="mt-0.5 text-[10px] text-text-tertiary">{data.store.address}</p>
              )}
              {/* #1880 建物名は住所本体の下に別行で (紙のレシートと同じ) */}
              {data.store.building && (
                <p className="text-[10px] text-text-tertiary">{data.store.building}</p>
              )}
              {data.store.phone && (
                <p className="text-[10px] text-text-tertiary">TEL {data.store.phone}</p>
              )}
              {data.store.invoiceRegistrationNumber && (
                <p className="text-[10px] text-text-tertiary">
                  {t('receipt.invoiceRegNo')} {data.store.invoiceRegistrationNumber}
                </p>
              )}
            </div>
            <div className="mt-3 flex justify-between border-t border-dashed border-gray-300 pt-3 text-[11px] text-text-secondary">
              <span>{formatDateTime(data.paidAt)}</span>
              <span>No. {data.slipNo}</span>
            </div>

            {/* #1217 店頭注文 (カウンター受け取り): 呼び出し番号を大きく + 受け取り状況
                ページへの導線。アプリ不要の客がこのレシートだけで番号を追える。 */}
            {data.callNumber && (
              <div className="mt-3 rounded-xl border border-matcha/30 bg-matcha/5 p-3 text-center">
                <p className="text-[11px] text-text-secondary">{t('receipt.callNumberLabel')}</p>
                <p className="font-display text-3xl font-bold text-matcha">{data.callNumber}</p>
                {data.statusUrl && (
                  <a
                    href={data.statusUrl}
                    className="mt-1 inline-block text-[11px] text-navy underline print:hidden"
                  >
                    {t('receipt.checkPickupStatus')}
                  </a>
                )}
              </div>
            )}

            {/* 明細 (あれば品目、無ければ摘要1行 — メモ #1205 or ご利用代金) */}
            <div className="mt-3 space-y-1.5 border-t border-dashed border-gray-300 pt-3">
              {items.length > 0 ? (
                items.map((i, idx) => (
                  <div key={idx} className="flex justify-between gap-2">
                    <span className="text-text-primary">
                      {itemName(i)}
                      {(i.quantity || 1) > 1 && (
                        // #1880 単価。紙のレシートと揃える — 2 点以上のとき、
                        // 合計だけでは**何がいくらだったか復元できない**。
                        // 1 点なら単価 = 合計なので数量も単価も出さない。
                        <span className="ml-1 text-text-tertiary">
                          @¥{(i.price || 0).toLocaleString()} ×{i.quantity}
                        </span>
                      )}
                      {/* #2031 紙のレシートと同じく、品名の下に選んだ内容を出す */}
                      <VariationLine item={i} />
                    </span>
                    <span className="shrink-0 text-text-primary">
                      ¥{((i.price || 0) * (i.quantity || 1)).toLocaleString()}
                    </span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between gap-2">
                  <span className="text-text-primary">{data.memo || t('receipt.summaryDefault')}</span>
                  <span className="shrink-0 text-text-primary">¥{data.amountJpy.toLocaleString()}</span>
                </div>
              )}
            </div>

            <div className="mt-3 border-t border-gray-300 pt-3">
              <div className="flex justify-between font-bold text-text-primary">
                <span>{t('receipt.total')}</span>
                <span className="font-display text-lg">¥{data.amountJpy.toLocaleString()}</span>
              </div>
              {/* 内税は適格請求書発行事業者のみ (#1169 — 免税事業者は誤認防止で出さない)。
                  #1171 税率ごとに区分した対象額 + 消費税額 (適格請求書の記載事項④⑤)。 */}
              {data.store.invoiceRegistrationNumber && (taxRows.length > 0) && (
                <div className="mt-0.5 space-y-0.5 text-right text-[10px] text-text-tertiary">
                  {taxRows.map((row) => (
                    <p key={row.rate}>
                      {row.rate === 8 ? '※' : ''}
                      {t('receipt.taxRateRow', {
                        rate: String(row.rate),
                        subtotal: row.subtotal.toLocaleString(),
                        tax: row.tax.toLocaleString(),
                      })}
                    </p>
                  ))}
                  {hasReduced && <p>{t('receipt.reducedTaxLegend')}</p>}
                </div>
              )}
              {data.refunded && (
                <p className="mt-1 text-right text-[10px] text-red-500">
                  {t('receipt.refundedNote')}
                </p>
              )}
            </div>

            {/* 決済情報 (売上票の統合欄 — EMV は #1205) */}
            <div className="mt-3 space-y-1 border-t border-dashed border-gray-300 pt-3 text-[11px] text-text-secondary">
              <div className="flex justify-between">
                <span>{t('receipt.paymentMethodLabel')}</span>
                <span>
                  {isCard
                    ? `${t('payment.methodStripe')}${cardSummary}`
                    : data.paymentMethod === 'cash'
                      ? t('payment.methodCash')
                      : data.paymentMethod === 'store_terminal'
                        ? t('payment.methodStoreTerminal')  // #2162 お店の端末 (STORES 決済・Airペイなど)
                        : t('payment.methodPoints')}
                </span>
              </div>
              {data.paymentNo && (
                <div className="flex justify-between">
                  <span>{t('receipt.paymentNo')}</span>
                  <span className="font-mono">{data.paymentNo}</span>
                </div>
              )}
              {data.authorizationCode && (
                <div className="flex justify-between">
                  <span>{t('receipt.authNo')}</span>
                  <span className="font-mono">{data.authorizationCode}</span>
                </div>
              )}
              {emv?.applicationPreferredName && (
                <div className="flex justify-between">
                  <span>{t('receipt.emvApp')}</span>
                  <span>{emv.applicationPreferredName}</span>
                </div>
              )}
              {emv?.dedicatedFileName && (
                <div className="flex justify-between">
                  <span>AID</span>
                  <span className="font-mono">{emv.dedicatedFileName}</span>
                </div>
              )}
              {emv?.accountType && (
                <div className="flex justify-between">
                  <span>{t('receipt.emvAccountType')}</span>
                  <span>{t(ACCOUNT_TYPE_KEYS[emv.accountType] || '') || emv.accountType}</span>
                </div>
              )}
            </div>
          </div>
          <p className="mt-3 text-center font-body text-[11px] text-text-tertiary">
            {t(usesReceiptWord(data.store.genre) ? 'receipt.digitalNote' : 'receipt.digitalNoteStatement')}
          </p>
        </div>
      )}

      {tab === 'ryoshusho' && (
        <>
          {/* 入力欄 (画面のみ) */}
          <div className="px-5 mt-4 space-y-3 print:hidden">
            <div>
              <label className="block font-body text-xs text-text-secondary mb-1">
                {t('receipt.addresseeLabel')}
              </label>
              <input
                type="text"
                value={addressee}
                onChange={(e) => setAddressee(e.target.value)}
                placeholder={t('receipt.addresseePlaceholder')}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-navy focus:outline-none"
              />
            </div>
            <div>
              <label className="block font-body text-xs text-text-secondary mb-1">
                {t('receipt.purposeLabel')}
              </label>
              <input
                type="text"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm focus:border-navy focus:outline-none"
              />
            </div>
          </div>

          {/* 領収書本体 (常に日本語 — 日本の経費精算用の定型書式) */}
          <div className="px-5 mt-5 pb-6">
            <div id="web-receipt-doc" className="rounded-xl bg-white shadow-card border border-gray-200 p-6">
              <h2 className="text-center font-display text-xl font-bold tracking-[0.5em] text-text-primary">
                領収書
              </h2>
              <p className="mt-1 text-center font-body text-[10px] text-text-tertiary">RECEIPT</p>

              <div className="mt-4 flex items-end justify-between">
                <p className="font-body text-base text-text-primary border-b border-gray-400 min-w-[55%] pb-0.5">
                  {addressee || ' '}
                  <span className="ml-2 text-sm">様</span>
                </p>
                <div className="text-right font-body text-[11px] text-text-secondary">
                  <p>No. {data.slipNo}</p>
                  <p>{formatDateJa(data.paidAt)}</p>
                </div>
              </div>

              <div className="mt-6 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-center">
                <p className="font-display text-2xl font-bold text-text-primary">
                  ￥{data.amountJpy.toLocaleString()}
                  <span className="ml-1 text-base">-</span>
                </p>
                {/* 内税表記は適格請求書発行事業者 (登録番号設定済み) の店のみ。
                    免税事業者に税額を出すと適格請求書と誤認されるため (#1169)。 */}
                {data.store.invoiceRegistrationNumber ? (
                  <div className="mt-0.5 font-body text-[10px] text-text-tertiary">
                    <p>(税込)</p>
                    {taxRows.map((row) => (
                      <p key={row.rate}>
                        {row.rate === 8 ? '※' : ''}
                        {row.rate}%対象 ￥{row.subtotal.toLocaleString()}
                        （内消費税額 ￥{row.tax.toLocaleString()}）
                      </p>
                    ))}
                    {hasReduced && <p>※印は軽減税率対象商品</p>}
                  </div>
                ) : (
                  <p className="mt-0.5 font-body text-[10px] text-text-tertiary">(税込)</p>
                )}
              </div>

              <p className="mt-4 font-body text-sm text-text-primary">
                但し <span className="border-b border-gray-400 px-1">{purpose || ' '}</span>
              </p>
              <p className="mt-2 font-body text-sm text-text-primary">
                上記正に領収いたしました
              </p>

              {isCard && (
                <p className="mt-2 font-body text-xs font-medium text-text-secondary">
                  （クレジットカード利用{cardSummary}）
                </p>
              )}
              {data.refunded && (
                <p className="mt-1 font-body text-xs text-red-500">
                  （一部返金後の実受領額で発行）
                </p>
              )}

              <div className="mt-6 flex items-end justify-between gap-3">
                <p className="font-body text-[10px] text-text-tertiary leading-relaxed">
                  電子発行につき収入印紙は不要です
                </p>
                <div className="text-right font-body text-xs text-text-primary leading-relaxed">
                  <p className="font-semibold">{data.store.name || 'YOKOSO PAY'}</p>
                  {data.store.address && <p className="text-[10px] text-text-secondary">{data.store.address}</p>}
                  {data.store.building && <p className="text-[10px] text-text-secondary">{data.store.building}</p>}
                  {data.store.phone && <p className="text-[10px] text-text-secondary">TEL {data.store.phone}</p>}
                  {data.store.invoiceRegistrationNumber && (
                    <p className="text-[10px] text-text-secondary">
                      登録番号 {data.store.invoiceRegistrationNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={() => window.print()}
              className="tap-effect mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-navy py-3 font-body text-sm font-bold text-white print:hidden"
            >
              <Printer size={16} />
              {t('receipt.printButton')}
            </button>
            <p className="mt-2 text-center font-body text-[11px] text-text-tertiary print:hidden">
              {t('receipt.printHint')}
            </p>
          </div>
        </>
      )}
    </div>
  )
}
